import { FC } from 'react';

import { RefreshCw } from 'lucide-react';

import { SellerOfferProductCard } from './SellerOfferProductCard';
import { InfiniteScrollTrigger } from '../../components/shared/InfiniteScrollTrigger';
import { Button } from '../../components/ui/Button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/Select';
import { TooltipProvider } from '../../components/ui/Tooltip';
import { Offer, OfferProperty, ProductType } from '../../types';

interface Props {
    readonly areOffersEmpty: boolean;
    readonly offers: Offer[];
    readonly offerProperties: OfferProperty[];
    readonly nextPage: number | null;
    readonly loading: boolean;
    readonly loadingMore: boolean;
    readonly loadMore: () => void;
    readonly resetFilters: () => void;
    readonly sort: string;
    readonly onSortChange: (value: string) => void;
    readonly currentProductType?: ProductType | null;
    readonly heading?: string;
    readonly resultsCount?: number;
    readonly subtitle?: string;
    readonly searchQuery?: string;
    readonly clearSearch?: () => void;
    readonly withDesktopSidebar?: boolean;
}

const getOfferCountText = (count: number): string => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return `${count} пропозицій`;
    }

    if (lastDigit === 1) {
        return `${count} пропозиція`;
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return `${count} пропозиції`;
    }

    return `${count} пропозицій`;
};

export const SellerOffersGrid: FC<Props> = ({
    areOffersEmpty,
    offers,
    offerProperties,
    nextPage,
    loading,
    loadingMore,
    loadMore,
    resetFilters,
    sort,
    onSortChange,
    currentProductType,
    heading: headingOverride,
    resultsCount,
    subtitle,
    searchQuery,
    clearSearch,
    withDesktopSidebar = false,
}) => {
    const heading = headingOverride || currentProductType?.name || 'Усі товари';

    if (areOffersEmpty) {
        return (
            <div className="mx-auto flex flex-col items-center justify-center px-4 py-12 text-center">
                <div className="mb-4 rounded-full bg-muted/50 p-4"></div>
                <h3 className="mb-2 text-xl font-medium">Товари не знайдено</h3>
                <p className="mb-6 max-w-md text-muted-foreground">
                    На жаль, обраний тип товарів відсутній в наявності
                </p>
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={300}>
            <div className="flex-1">
                <div className="mb-4 flex flex-col justify-between gap-4 sm:mb-6 sm:flex-row sm:items-center">
                    <div className="space-y-1">
                        <h2
                            className="text-lg font-bold sm:text-2xl md:block md:text-xl"
                            data-testid="products-grid-heading"
                        >
                            {heading}
                        </h2>

                        {(subtitle || typeof resultsCount === 'number') && (
                            <p className="text-sm text-muted-foreground">
                                {subtitle}
                                {subtitle && typeof resultsCount === 'number'
                                    ? ' • '
                                    : ''}
                                {typeof resultsCount === 'number'
                                    ? getOfferCountText(resultsCount)
                                    : ''}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm whitespace-nowrap text-muted-foreground">
                            Сортувати за:
                        </span>
                        <Select
                            defaultValue={sort || 'newest'}
                            onValueChange={onSortChange}
                        >
                            <SelectTrigger className="h-9 w-full rounded-xl text-sm sm:w-[160px] [&_svg]:h-3.5 [&_svg]:w-3.5">
                                <SelectValue placeholder="Новизною" />
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl text-sm">
                                <SelectItem
                                    className="py-1 text-sm"
                                    value="newest"
                                >
                                    Новизною
                                </SelectItem>
                                <SelectItem
                                    className="py-1 text-sm"
                                    value="price-low"
                                >
                                    Ціна: від низької до високої
                                </SelectItem>
                                <SelectItem
                                    className="py-1 text-sm"
                                    value="price-high"
                                >
                                    Ціна: від високої до низької
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {offers.length > 0 ? (
                    <div
                        className={`grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 ${
                            withDesktopSidebar
                                ? 'lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5'
                                : 'lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6'
                        }`}
                        data-testid="products-grid"
                    >
                        {offers.map((offer, index) => (
                            <SellerOfferProductCard
                                key={offer.id}
                                offer={offer}
                                offerProperties={offerProperties}
                                loading={loading}
                                eagerLoadImage={index < 8}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                        <div className="mb-4 rounded-full bg-muted/50 p-4"></div>
                        <h3 className="mb-2 text-xl font-medium">
                            Товари не знайдено
                        </h3>
                        <p className="mb-6 max-w-md text-muted-foreground">
                            {searchQuery
                                ? `У продавця немає пропозицій за запитом «${searchQuery}». Перевірте написання або спробуйте коротший запит.`
                                : 'На жаль, нічого не знайдено за вашими критеріями пошуку. Спробуйте змінити фільтри або перегляньте інші категорії.'}
                        </p>
                        <Button
                            variant="outline"
                            className="gap-2"
                            onClick={searchQuery ? clearSearch : resetFilters}
                        >
                            <RefreshCw className="h-4 w-4" />
                            {searchQuery ? 'Очистити пошук' : 'Скинути фільтри'}
                        </Button>
                    </div>
                )}

                {nextPage && (
                    <InfiniteScrollTrigger
                        className="mt-8"
                        loading={loading || loadingMore}
                        onLoadMore={loadMore}
                    />
                )}
            </div>
        </TooltipProvider>
    );
};
